import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EndorsementMotivationClient, EndorsementMotivationDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getApiClient = () => new EndorsementMotivationClient(API_BASE_URL, axiosInstance);

export const useEndorsementMotivations = () =>
  useQuery({
    queryKey: ['endorsementMotivations'],
    queryFn: async () => {
      const response = await getApiClient().searchEndorsementMotivations({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

export const useEndorsementMotivationSearch = () => {
  const search = async (params: any) => {
    const response = await getApiClient().searchEndorsementMotivations(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

export const useEndorsementMotivationById = (id: string | undefined) =>
  useQuery({
    queryKey: ['endorsementMotivation', id],
    queryFn: async () => {
      const response = await getApiClient().getEndorsementMotivationById(id!);
      return response?.data || null;
    },
    enabled: !!id,
  });

export const useCreateEndorsementMotivation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EndorsementMotivationDTO) => getApiClient().createEndorsementMotivation(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endorsementMotivations'] }),
  });
};

export const useUpdateEndorsementMotivation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EndorsementMotivationDTO }) =>
      getApiClient().updateEndorsementMotivation(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endorsementMotivations'] }),
  });
};

export const useDeleteEndorsementMotivation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getApiClient().deleteEndorsementMotivation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endorsementMotivations'] }),
  });
};